import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { THEME_NAMES, THEMES } from "./themes";
import { useTheme } from "./ThemeContext";

export function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [controlWidth, setControlWidth] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);
  const activeTheme = THEMES[theme];

  useLayoutEffect(() => {
    if (!sizerRef.current) return;

    const nextWidth = Math.ceil(sizerRef.current.getBoundingClientRect().width);
    setControlWidth((current) => (current === nextWidth ? current : nextWidth));
  }, []);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div
      className="relative"
      ref={rootRef}
      style={controlWidth ? { width: controlWidth } : undefined}
    >
      <div
        aria-hidden
        className="invisible pointer-events-none absolute right-0 top-0 w-max rounded-b-md border border-t-0 border-edge bg-surface-glass p-0.5"
        ref={sizerRef}
      >
        {THEME_NAMES.map((name) => (
          <div
            className="block whitespace-nowrap rounded px-2 py-1.5 text-left text-xs font-medium"
            key={name}
          >
            {THEMES[name].label}
          </div>
        ))}
      </div>

      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className={`w-full whitespace-nowrap border px-2 py-1.5 text-left text-xs font-medium transition-colors ${
          open
            ? "rounded-t-md rounded-b-none border-edge border-b-transparent bg-surface-glass text-primary backdrop-blur"
            : "rounded-md border-transparent bg-transparent text-muted hover:border-edge hover:bg-surface-glass hover:text-primary"
        }`}
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        {activeTheme.label}
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-30 -mt-px rounded-b-md border border-t-0 border-edge bg-surface-glass p-0.5 shadow-[var(--wt-shadow)] backdrop-blur"
          role="menu"
        >
          {THEME_NAMES.map((name) => (
            <button
              aria-checked={theme === name}
              className={`block w-full whitespace-nowrap rounded px-2 py-1.5 text-left text-xs font-medium transition-colors ${
                theme === name
                  ? "bg-accent text-accent-text"
                  : "text-muted hover:bg-surface-raised hover:text-primary"
              }`}
              key={name}
              onClick={() => {
                setTheme(name);
              }}
              role="menuitemradio"
              type="button"
            >
              {THEMES[name].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
